import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, TextField, Button, Typography, IconButton, Box, InputAdornment } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ManufacturingImg from '../../assets/ManufacturingImg.png';
import { useDispatch, useSelector } from "react-redux";
import { clearLoginData, loginAction } from "../../store/actions";

function LoginDialog({ open, onClose, onForgotPassword, onOtpLogin, onLoginSuccess, onLoginError }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const { successData: loginData, error: loginError, errorInfo } = useSelector((state) => state.loginReducer);

  const handleTogglePassword = () => setShowPassword(prev => !prev);

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      password: Yup.string()
        .min(6, "Password must be at least 6 characters")
        .required("Password is required"),
    }),
    onSubmit: (values) => {
      dispatch(loginAction(values));
    }
  });

  useEffect(() => {
    if (loginData) {
      localStorage.setItem('userDetails', JSON.stringify(loginData.response));
      if (onLoginSuccess) {
        onLoginSuccess();
      }
    } else if (loginError && onLoginError) {
      onLoginError(errorInfo?.message || "Network error. Please check your connection and try again.");
    }

    return () => {
      dispatch(clearLoginData());
    };
  }, [loginData, loginError, errorInfo, navigate, onLoginSuccess, onLoginError, dispatch]);

  const textFieldStyle = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '20px',
      width: '300px',
      maxWidth: '300px',
    },
    '& .MuiInputAdornment-root': {
      '& .MuiIconButton-root': {
        color: '#007bff',
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      disableScrollLock={true}
      fullWidth
      maxWidth="md"
      sx={{ "& .MuiDialog-paper": { display: "flex", flexDirection: { xs: "column",sm:"row", md: "row" }, width: { xs: "90%", md: "900px",sm:"600px" }, borderRadius: "15px", overflow: "hidden" } }}
    >
      <Box sx={{ width: "50%", backgroundColor: "#f8f9fa", display: { xs: "none", sm: "flex",md:"flex" }, alignItems: "center", justifyContent: "center" }}>
        <img src={ManufacturingImg} alt="Login Illustration" style={{ width: "100%" }} />
      </Box>
      <Box sx={{ width: { xs: "100%", md: "50%" }, padding: { xs: '5px', sm: "40px",md:"40px" }, display: "flex", flexDirection: "column", justifyContent: "center", position: "relative" }}>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle sx={{ textAlign: "center" }}>
          <Typography sx={{ fontSize: "24px", fontWeight: "bold", color: "#31303b" }}>Hello Again!</Typography>
          <Typography sx={{ color: "gray" }}>Welcome Back</Typography>
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <form onSubmit={formik.handleSubmit} style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
            <TextField
              autoFocus
              margin="dense"
              id="email"
              name="email"
              label="Email Address"
              type="email"
              variant="outlined"
              size="small"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              sx={textFieldStyle}
            />
            <TextField
              margin="dense"
              id="password"
              name="password"
              label="Password"
              type={showPassword ? "text" : "password"}
              variant="outlined"
              size="small"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              sx={textFieldStyle}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton aria-label="toggle password visibility" onClick={handleTogglePassword} edge="end">
                      {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ width: "100%", display: "flex", justifyContent: "flex-end", mb: 2, mr: { xs: 0, sm: 8 } }}>
              <Button style={{ textDecoration: "none", color: "#007bff", padding: "0px" }} onClick={onForgotPassword}>
                Forgot Password?
              </Button>
            </Box>
            <Button
              type="submit"
              sx={{ backgroundColor: "#007bff", color: "#fff", borderRadius: '15px', p: '10px', width: "100%", maxWidth: "300px", mb: 2 }}>
              Login
            </Button>
            <Button
              style={{
                textDecoration: "none",
                color: "gray",
                marginBottom: "10px",
                padding: "0px",
              }}
              onClick={onOtpLogin}
            >
              Use OTP to Login
            </Button>
          </form>
        </DialogContent>
      </Box>
    </Dialog>
  );
}

export default LoginDialog;
